import { useEffect, useState } from "react";
import apiService, { toastError, toastSuccess } from "services/apiService";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shippingPrices, setShippingPrices] = useState([]);

  const [isLoadingPriceConfig, setIsLoadingPriceConfig] = useState(true);
  const [priceConfigError, setPriceConfigError] = useState(null);
  const [priceConfig, setPriceConfig] = useState(null);

  const [selectedTab, setSelectedTab] = useState(0);

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    // Fetch products based on filters
    const fetchSP = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await apiService.fetchData(
          "/atemo/shippingPrices"
        );

        if (error || !Array.isArray(data)) {
          const message =
            (typeof error === "string" && error) ||
            error?.message ||
            "Impossible de récupérer les frais de ports.";
          console.error(
            "Erreur lors de la récupération des frais de ports :",
            message
          );
          setError(message);
          toastError(message);
          setShippingPrices([]);
          return;
        }

        setError(null);
        setShippingPrices(
          data.map((item) => ({
            ...item,
            price: item.price?.toString() ?? "",
          }))
        );
      } catch (error) {
        const message = error?.message || "Une erreur est survenue";
        console.error(
          "Erreur lors de la récupération des frais de ports :",
          message
        );
        setError(message);
        toastError(message);
        setShippingPrices([]);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchPriceConfig = async () => {
      setIsLoadingPriceConfig(true);
      try {
        const { data, error } = await apiService.fetchData(
          "/atemo/priceConfig"
        );

        if (error) {
          const message =
            (typeof error === "string" && error) ||
            error?.message ||
            "Impossible de récupérer la configuration des prix recommandées.";
          console.error(
            "Erreur lors de la récupération de la configuration des prix recommandées :",
            message
          );
          setPriceConfigError(message);
          toastError(message);
          setPriceConfig(null);
          return;
        }

        setPriceConfigError(null);
        setPriceConfig(data);
      } catch (error) {
        const message = error?.message || "Une erreur est survenue";
        console.error(
          "Erreur lors de la récupération de la configuration des prix recommandées :",
          message
        );
        setPriceConfigError(message);
        toastError(message);
        setPriceConfig(null);
      } finally {
        setIsLoadingPriceConfig(false);
      }
    };

    fetchSP();
    fetchPriceConfig();
  }, []);

  const handleValidateShippingPrices = async () => {
    try {
      const { error } = await apiService.postData("/atemo/shippingPrices", {
        prices: shippingPrices,
      });
      if (error) {
        toastError(error);
        setError(error);
      } else {
        toastSuccess("Frais de port mis à jour !");
        setError(null);
      }
    } catch (error) {
      toastError(error);
      setError(error);
      console.error(
        "Erreur lors de la mise à jour des frais de ports :",
        error
      );
    }
  };

  const handleValidatePriceConfig = async () => {
    try {
      const { error } = await apiService.postData("/atemo/priceConfig", {
        ...priceConfig,
      });
      if (error) {
        toastError(error);
        setPriceConfigError(error);
      } else {
        toastSuccess("Configuration des prix mise à jour !");
        setPriceConfigError(null);
      }
    } catch (error) {
      toastError(error);
      setPriceConfigError(error);
      console.error(
        "Erreur lors de la mise à jour de la configuration des prix recommandées :",
        error
      );
    }
  };

  const handleValidate = () => {
    if (selectedTab === 0) {
      handleValidateShippingPrices();
    } else if (selectedTab === 1) {
      handleValidatePriceConfig();
    }
  };

  const handlePriceChange = (id, value) => {
    setShippingPrices((current) =>
      current.map((item) =>
        item._id === id
          ? {
              ...item,
              price: value,
            }
          : item
      )
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!shippingPrices.length) {
      return <Alert severity="info">Aucun frais de port disponible.</Alert>;
    }

    return (
      <>
        <Typography>Configuration des frais de port :</Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {shippingPrices.map(({ _id, category, price }) => (
            <TextField
              label={`Catégorie ${category} (€)`}
              value={price}
              type="number"
              onChange={(event) => handlePriceChange(_id, event.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              size="small"
            />
          ))}
        </Box>
      </>
    );
  };

  const renderPriceConfigContent = () => {
    if (isLoadingPriceConfig) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box display="flex" flexWrap="wrap" gap={2}>
        {priceConfigError && <Alert severity="error">{priceConfigError}</Alert>}
        {/* Render price configuration details here */}
        <Typography>Configuration des prix recommandées :</Typography>
        <TextField
          fullWidth
          label="Prix Minimum"
          placeholder="valeur du prix minimum"
          type="number"
          value={priceConfig?.minPrice || ""}
          onChange={(event) =>
            setPriceConfig({
              ...priceConfig,
              minPrice: Number(event.target.value),
            })
          }
          slotProps={{ htmlInput: { min: 1 } }}
          size="small"
        />
        <Divider sx={{ height: "1px" }} fullWidth />

        <FormControl fullWidth>
          <InputLabel id="select-price-choice">Choix de prix</InputLabel>
          <Select
            value={priceConfig?.priceChoice || ""}
            label="Choix de prix"
            onChange={(event) =>
              setPriceConfig({
                ...priceConfig,
                priceChoice: event.target.value,
              })
            }
          >
            <MenuItem value="MINIMUM">Minimum</MenuItem>
            <MenuItem value="MEDIAN">Médian</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="select-discount-type">Type de remise</InputLabel>
          <Select
            value={priceConfig?.discountType || ""}
            label="Type de remise"
            onChange={(event) =>
              setPriceConfig({
                ...priceConfig,
                discountType: event.target.value,
              })
            }
          >
            <MenuItem value="PERCENTAGE">Pourcentage</MenuItem>
            <MenuItem value="FIXED">Fixe</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Valeur"
          placeholder="valeur de la remise"
          type="number"
          value={priceConfig?.value || ""}
          onChange={(event) =>
            setPriceConfig({
              ...priceConfig,
              value: Number(event.target.value),
            })
          }
          slotProps={{ htmlInput: { min: 1 } }}
          size="small"
        />
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(priceConfig?.includeCarecoInMarketPrices)}
              onChange={(event) =>
                setPriceConfig({
                  ...priceConfig,
                  includeCarecoInMarketPrices: event.target.checked,
                })
              }
            />
          }
          label="Inclure CARECO dans les prix marché"
        />
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Typography variant="h5" fontWeight={600}>
        Paramètres
      </Typography>
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        aria-label="basic tabs example"
      >
        <Tab label="FRAIS DE PORT" id="tabs-fdp" />
        <Tab label="CONFIGURATION DES PRIX" id="tabs-cdp" />
      </Tabs>
      {selectedTab === 0 && renderContent()}
      {selectedTab === 1 && renderPriceConfigContent()}
      <Button variant="contained" onClick={() => handleValidate()}>
        Valider
      </Button>
    </Box>
  );
};

export default SettingsPage;
